import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, purchaseSeatsLanguages } from "./helpers";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import BackgroundImage from "public/images/springfieldcardinals/springfieldcards.jpeg";
import StaticSeatMap from "public/images/springfieldcardinals/3D-Seating-Chart-MPV-2025.png";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const FindGamesBannerImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/springfield-cardinals-banner.png";

const config: TeamConfig = {
  teamSchema: "springfieldcardinals_flex",
  browserTitle: "Springfield Cardinals Flex Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Springfield Cardinals",

  // Seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        discount: 20,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your tickets! Add 1 more game for 30% off or 2+ games for 40% off.",
      },
      {
        id: 2,
        discount: 30,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        customProgressBarMessage:
          "Congrats! You have unlocked 30% off your tickets! Add 1 more game for 40% off your seats or checkout now.",
      },
      {
        id: 3,
        discount: 40,
        minSelectedAmount: 4,
        updatedAccountGroup: 2,
        customProgressBarMessage:
          "Congrats! You have unlocked 40% off your seats! Add more games for checkout now.",
      },
    ],
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  oktaClientId: "0oasmcdgh92Jom7TI357",
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  platform: vendorUUIDs.tickesdotcom,
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=SPCM_MYTIXX&orgid=57050#/auth/login",

  // Images
  images: {
    findGamesBannerImage: FindGamesBannerImage,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/springfield-cardinals-logo.png?width=128&height=75",
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/springfield-cardinals-logo.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    progressBar: {
      staticProgressBarText: false,
    },

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:4178630395"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (417) 863-0395
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#d30e45",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    purchaseGiftCard: null,

    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Springfield Cardinals tickets are confirmed! You will be notified when your tickets are available to be managed.",

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
